import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, HeartPulse } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Field, EmptyState } from "@/components/Field";

const STATUS = { attiva: "bg-amber-50 text-amber-700", controllata: "bg-[#edf2ef] text-[#2f4f47]", risolta: "bg-emerald-50 text-emerald-700" };
const blank = () => ({ name: "", date: "", status: "attiva", notes: "" });

export function PathologyList({ items, onDelete, prefix = "pathology" }) {
  return (
    <div className="space-y-3">
      {items.map((x) => (
        <div key={x.id} className="flex items-start gap-4 rounded-2xl border border-[#e2e8f0] bg-white p-4" data-testid={`${prefix}-item-${x.id}`}>
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-rose-50 text-rose-500"><HeartPulse className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{x.name}</span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS[x.status] || STATUS.attiva}`}>{x.status}</span>
            </div>
            <div className="text-xs text-slate-500">Data: {fmtDate(x.date)}</div>
            {x.notes && <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{x.notes}</p>}
          </div>
          {onDelete && <button className="btn-ghost text-slate-400 hover:text-rose-600" onClick={() => onDelete(x.id)} data-testid={`${prefix}-delete-${x.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
    </div>
  );
}

export function PathologiesTab({ rec, reload }) {
  const [f, setF] = useState(blank());
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/patients/${rec.patient.id}/pathologies`, f);
      toast.success("Patologia aggiunta");
      setF(blank());
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa voce?")) return;
    await api.delete(`/pathologies/${id}`);
    reload();
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <form onSubmit={add} className="card h-fit space-y-4" data-testid="pathology-form">
        <h3 className="text-lg font-bold">Aggiungi patologia</h3>
        <Field label="Patologia / condizione"><input required className="field" value={f.name} onChange={set("name")} placeholder="Es. Tiroidite di Hashimoto" data-testid="pathology-name-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Data diagnosi"><input type="date" className="field" value={f.date} onChange={set("date")} data-testid="pathology-date-input" /></Field>
          <Field label="Stato">
            <select className="field" value={f.status} onChange={set("status")} data-testid="pathology-status-select">
              <option value="attiva">Attiva</option><option value="controllata">Controllata</option><option value="risolta">Risolta</option>
            </select>
          </Field>
        </div>
        <Field label="Note"><textarea className="field min-h-[80px]" value={f.notes} onChange={set("notes")} placeholder="Terapie, esami, intolleranze…" data-testid="pathology-notes-input" /></Field>
        <button className="btn-primary w-full" data-testid="pathology-add-button"><Plus className="h-4 w-4" /> Aggiungi</button>
      </form>
      <div className="lg:col-span-2">
        {rec.pathologies.length ? <PathologyList items={rec.pathologies} onDelete={remove} /> : <EmptyState icon={HeartPulse} title="Nessuna patologia registrata" testid="pathologies-empty" />}
      </div>
    </div>
  );
}
